import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, Building, Award, MapPin, Briefcase } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const OurExperience = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const industries = [
    'Oil & Gas',
    'Power Generation',
    'Water Treatment',
    'Manufacturing',
    'Petrochemical',
    'Marine & Offshore',
    'Food & Beverage',
    'Pharmaceutical'
  ];

  const projectTypes = [
    { title: 'Equipment Repair', count: '500+', description: 'Pumps, valves, and rotating equipment restored' },
    { title: 'Tank Linings', count: '200+', description: 'Storage and process tanks protected' },
    { title: 'Pipe Repairs', count: '1,000+', description: 'Leaks sealed and systems protected' },
    { title: 'Structural Repairs', count: '300+', description: 'Concrete and steel structures restored' }
  ];

  const capabilities = [
    'ASME and API code compliance',
    'Confined space entry certified',
    'Working at heights qualified',
    'Hazardous environment trained',
    'Quality management systems',
    'Safety management programs'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["about/our-experience"]?.title} description={seoData["about/our-experience"]?.description} path="/about/our-experience" keywords={seoData["about/our-experience"]?.keywords || []} />
      
      <PageHero
        badge="About BCS"
        title="Our Experience"
        description="With over 25 years in industrial repair and protection, BCS has built a reputation for excellence. Our experience spans thousands of projects across diverse industries."
        backgroundKey="our-experience"
        buttonText="Discuss Your Project"
      />

      {/* Project Experience */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">Project Experience</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {projectTypes.map((project, index) => (
              <div key={index} className="bg-blue-800 p-6 rounded-lg text-center">
                <div className="text-4xl font-bold mb-2">{project.count}</div>
                <div className="text-lg font-semibold mb-2">{project.title}</div>
                <div className="text-blue-200 text-sm">{project.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Industries We Serve</h2>
              <p className="text-gray-600 mb-8">
                Our experience spans virtually every industrial sector. We understand the unique challenges 
                and requirements of each industry and deliver solutions that meet the highest standards.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {industries.map((industry, index) => (
                  <div key={index} className="flex items-center bg-slate-50 p-3 rounded-lg">
                    <Building className="text-blue-900 mr-2 flex-shrink-0" size={18} />
                    <span className="text-gray-700">{industry}</span>
                  </div>
                ))}
              </div>
              <Button className="mt-6" variant="outline" onClick={() => navigate('/#industries')}>
                View All Industries <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
            <div className="bg-slate-800 text-white p-8 rounded-lg">
              <Award className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Technical Capabilities</h3>
              <p className="text-slate-300 mb-6">
                Our team maintains certifications and qualifications to work in the most demanding environments.
              </p>
              <div className="space-y-2">
                {capabilities.map((capability, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="text-green-400 mr-2 flex-shrink-0" size={16} />
                    <span className="text-slate-200 text-sm">{capability}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto text-center">
          <MapPin className="mx-auto text-blue-900 mb-4" size={48} />
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Nationwide Service</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            While headquartered in Southern California, we provide services throughout the United States. 
            Our mobile crews are ready to deploy wherever your facility is located.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Badge className="bg-blue-100 text-blue-900 text-lg px-4 py-2">West Coast</Badge>
            <Badge className="bg-blue-100 text-blue-900 text-lg px-4 py-2">Southwest</Badge>
            <Badge className="bg-blue-100 text-blue-900 text-lg px-4 py-2">Gulf Coast</Badge>
            <Badge className="bg-blue-100 text-blue-900 text-lg px-4 py-2">Midwest</Badge>
            <Badge className="bg-blue-100 text-blue-900 text-lg px-4 py-2">Nationwide</Badge>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-green-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Briefcase className="mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold mb-4">Put Our Experience to Work for You</h2>
          <p className="text-green-100 mb-8">
            Whatever your industrial repair challenge, we've likely solved it before. Let's discuss your project.
          </p>
          <Button size="lg" className="bg-white text-green-700 hover:bg-gray-100" onClick={scrollToContact}>
            Start a Conversation <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OurExperience;
